import React, { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import FireIcon from "../../Assets/fire.png";

// All stages the funnel understands, in order. The backend returns null for
// stages it can't measure yet (downloads live in the app stores, trials have
// no schema signal) — those are hidden until real tracking exists.
// `base: "root"` = benchmarked against the first visible stage in "overall"
// mode; `base: "paid"` keeps active premium as a % of subscribers.
const ALL_STAGES = [
  { key: "downloads",      apiKey: "downloads",      name: "Downloads",        desc: "App store installs",                 base: "root" },
  { key: "registered",     apiKey: "registered",     name: "Registered users", desc: "Accounts created",                   base: "root" },
  { key: "active",         apiKey: "active",         name: "Active users",     desc: "Completed a walk in the last 30 days", base: "root" },
  { key: "trial",          apiKey: "trial",          name: "Trial starts",     desc: "Started a premium trial (RevenueCat)", base: "root" },
  { key: "paid",           apiKey: "paid",           name: "Paid subscribers", desc: "Has an active premium subscription", base: "root" },
  { key: "activePremium",  apiKey: "active_premium", name: "Active premium",   desc: "Premium + walked in last 30 days",   base: "paid" },
];

// Benchmark defaults — starting points only; every value is editable and
// persisted. "step" = % of the previous visible stage, "overall" = % of the
// funnel's first stage (except active premium, always % of paid).
const DEFAULT_BENCH = {
  step:    { registered: 70, active: 15, trial: 33, paid: 30, activePremium: 70 },
  overall: { registered: 70, active: 15, trial: 5,  paid: 4,  activePremium: 70 },
};

const SUGGEST = {
  registered:    ["Signup friction", "Onboarding length", "Social login options"],
  active:        ["First-walk experience", "Push reminder timing", "Habit loop & streak nudges", "Post-signup empty state"],
  trial:         ["Paywall timing", "Premium value clarity", "Trial CTA placement"],
  paid:          ["Paywall conversion", "Pricing & plan mix", "Premium value clarity"],
  activePremium: ["Premium feature usage", "Premium retention & habit quality", "Renewal risk"],
};

const FUNNEL_COLORS = ["#DCE485", "#CFDB5C", "#C2D133", "#B9CC03", "#9AAD05", "#7C8A08"];
const BENCH_STORAGE_KEY = "tw-funnel-bench-v3";
// Expected count for the funnel's base stage, per range — null = use actual.
const BASE_TARGET_STORAGE_KEY = "tw-funnel-base-target-v1";
const DEFAULT_BASE_TARGETS = { all: null, "90d": null, "30d": null };
const RANGE_DAYS = { all: 0, "90d": 90, "30d": 30 };

const fmt = (n) => Math.round(n).toLocaleString("en-US");
const pct1 = (n) => Math.round(n * 10) / 10 + "%";
const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—";
const daysAgo = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5)) : null);

const statusOf = (ratio, isBase) => {
  if (isBase) return { label: "Baseline", bg: "#F2F3EA", fg: "#6B7051", dot: "#A6AC8B" };
  if (ratio >= 1.08) return { label: "Strong performance", bg: "#EEF4CD", fg: "#55660A", dot: "#8CA30B" };
  if (ratio >= 0.95) return { label: "On track", bg: "#E7F0FA", fg: "#2F6EA5", dot: "#3F88C5" };
  if (ratio >= 0.75) return { label: "Slightly below target", bg: "#FEF3E2", fg: "#B45309", dot: "#E8A33D" };
  return { label: "Major bottleneck", bg: "#FBEAEA", fg: "#C0392B", dot: "#D64545" };
};

const card = {
  background: "#fff",
  border: "1px solid #EFF1E0",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  padding: "20px 22px",
};

const selectStyle = {
  padding: "8px 10px",
  border: "1px solid #DDE2C4",
  borderRadius: 10,
  fontFamily: "inherit",
  fontSize: 13,
  background: "#fff",
  color: "#000",
  cursor: "pointer",
};

const TABLE_GRID = "minmax(230px,1.5fr) 118px 108px 124px 124px 138px 186px";
const USER_GRID = "minmax(170px,2fr) 92px 96px 70px 64px 52px 96px";

const FunnelAnalytics = () => {
  const [range, setRange] = useState("all");
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [benchMode, setBenchMode] = useState("step");
  const [bench, setBench] = useState(DEFAULT_BENCH);
  const [baseTargets, setBaseTargets] = useState(DEFAULT_BASE_TARGETS);

  const [drawer, setDrawer] = useState(null);
  const [drawerData, setDrawerData] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fPlan, setFPlan] = useState("All plans");
  const [sortBy, setSortBy] = useState("lastActive");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(BENCH_STORAGE_KEY));
      if (saved && typeof saved === "object") {
        setBench((s) => ({
          step: { ...s.step, ...(saved.step || {}) },
          overall: { ...s.overall, ...(saved.overall || {}) },
        }));
      }
    } catch (e) {}
    try {
      const savedBase = JSON.parse(localStorage.getItem(BASE_TARGET_STORAGE_KEY));
      if (savedBase && typeof savedBase === "object") {
        setBaseTargets((s) => ({ ...s, ...savedBase }));
      }
    } catch (e) {}
  }, []);

  // Fetch funnel counts whenever the range changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    AxiosInstance.get(`${ApiList.METRICS_FUNNEL}?days=${RANGE_DAYS[range]}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.s === 1) {
          setCounts(res.data.r.stages);
        } else {
          setError(res?.data?.m || "Failed to load funnel metrics");
        }
      })
      .catch((err) => !cancelled && setError(err?.message || "Failed to load funnel metrics"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range]);

  // Fetch the user segment when a stage drawer opens (or the range changes
  // while it's open).
  useEffect(() => {
    if (!drawer) return;
    const stage = ALL_STAGES.find((s) => s.key === drawer);
    if (!stage) return;
    let cancelled = false;
    setDrawerLoading(true);
    AxiosInstance.get(`${ApiList.METRICS_FUNNEL_USERS}?stage=${stage.apiKey}&days=${RANGE_DAYS[range]}`)
      .then((res) => {
        if (cancelled) return;
        setDrawerData(res?.data?.s === 1 ? res.data.r.users : []);
      })
      .catch(() => !cancelled && setDrawerData([]))
      .finally(() => !cancelled && setDrawerLoading(false));
    return () => { cancelled = true; };
  }, [drawer, range]);

  const setBenchValue = (key, raw) => {
    const v = Math.max(0, Math.min(100, parseFloat(raw) || 0));
    setBench((prev) => {
      const nb = { ...prev, [benchMode]: { ...prev[benchMode], [key]: v } };
      try { localStorage.setItem(BENCH_STORAGE_KEY, JSON.stringify(nb)); } catch (e) {}
      return nb;
    });
  };

  const setBaseTarget = (raw) => {
    const trimmed = String(raw).trim();
    const v = trimmed === "" ? null : Math.max(0, Math.round(parseFloat(trimmed) || 0));
    setBaseTargets((prev) => {
      const nt = { ...prev, [range]: v };
      try { localStorage.setItem(BASE_TARGET_STORAGE_KEY, JSON.stringify(nt)); } catch (e) {}
      return nt;
    });
  };

  const openDrawer = (key) => {
    setDrawer(key);
    setQ(""); setFPlan("All plans");
  };

  const activeBench = bench[benchMode];
  const baseTarget = baseTargets[range];

  // Only stages the backend can actually measure.
  const stages = useMemo(
    () => (counts ? ALL_STAGES.filter((s) => counts[s.apiKey] != null) : []),
    [counts]
  );
  const hiddenStages = counts ? ALL_STAGES.filter((s) => counts[s.apiKey] == null) : [];

  const comp = useMemo(() => {
    if (!counts || stages.length === 0) return [];
    const rootKey = stages[0].key;
    return stages.map((st, i) => {
      const actual = counts[st.apiKey];
      const isBase = i === 0;
      const baseKey = isBase
        ? null
        : benchMode === "step"
        ? stages[i - 1].key
        : st.base === "paid" && stages.some((s) => s.key === "paid")
        ? "paid"
        : rootKey;
      const baseStage = baseKey ? stages.find((s) => s.key === baseKey) : null;
      const baseActual = isBase ? actual : counts[baseStage.apiKey];
      // Each stage is graded in isolation against the ACTUAL count of its
      // base stage; the base-stage target only feeds stages benchmarked
      // directly against the base.
      const expectedBase = baseKey === rootKey ? (baseTarget ?? baseActual) : baseActual;
      const expected = isBase ? (baseTarget ?? actual) : Math.round((activeBench[st.key] / 100) * expectedBase);
      const actualPct = isBase ? 100 : baseActual > 0 ? (actual / baseActual) * 100 : 0;
      const ratio = expected > 0 ? actual / expected : 1;
      const gap = actual - expected;
      const hasTarget = !isBase || baseTarget != null;
      const status = statusOf(ratio, !hasTarget);
      const baseNote = isBase ? "" : "of " + baseStage.name.toLowerCase();
      return { ...st, i, actual, expected, actualPct, ratio, gap, isBase, hasTarget, status, baseNote };
    });
  }, [counts, stages, activeBench, benchMode, baseTarget]);

  const rootActual = comp.length > 0 ? comp[0].actual : 0;
  const scaleMax = Math.max(rootActual, baseTarget || 0, 1);
  const wOf = (n) => {
    const frac = Math.max(n / scaleMax, 0.0001);
    const w = Math.min(Math.sqrt(frac) * 100, 100);
    return Math.max(w, 6).toFixed(1) + "%";
  };

  // KPI cards from whatever stages exist.
  const kpis = [];
  if (counts) {
    const reg = counts.registered;
    comp.forEach((s) => {
      kpis.push({
        label: s.name,
        value: fmt(s.actual),
        sub: s.isBase
          ? s.desc.toLowerCase()
          : s.base === "paid" && counts.paid > 0
          ? pct1((s.actual / counts.paid) * 100) + " of subscribers"
          : reg > 0
          ? pct1((s.actual / reg) * 100) + " of registered"
          : "",
      });
    });
    if (reg > 0 && counts.paid != null) {
      kpis.push({ label: "Registered → paid", value: pct1((counts.paid / reg) * 100), sub: "overall conversion rate" });
    }
    if (reg > 0 && counts.active != null) {
      kpis.push({ label: "30-day active rate", value: pct1((counts.active / reg) * 100), sub: "of registered users" });
    }
  }

  // Bottleneck summary.
  const nonBase = comp.filter((s) => !s.isBase);
  const issues = nonBase.filter((s) => s.ratio < 0.95).sort((a, b) => a.ratio - b.ratio);
  const wins = nonBase.filter((s) => s.ratio >= 1.08);
  const worst = issues[0];
  let bnHeadline = "No major bottlenecks — funnel is on track.";
  let bnHeadlineBody = "Every stage is at or above its benchmark.";
  if (worst) {
    const prev = comp[worst.i - 1];
    bnHeadline = "Biggest drop-off: " + prev.name.toLowerCase() + " → " + worst.name.toLowerCase();
    bnHeadlineBody =
      "Only " + pct1(worst.actualPct) + " of " + worst.baseNote.replace("of ", "") + " reach this stage vs " +
      pct1(activeBench[worst.key]) + " expected — " + fmt(-worst.gap) + " users short of target.";
  }
  const bnItems = [];
  issues.slice(1).forEach((s) => {
    bnItems.push({
      dot: s.status.dot,
      title: s.name + ": " + pct1(s.actualPct) + " vs " + pct1(activeBench[s.key]) + " expected",
      body: fmt(-s.gap) + " users short of target.",
      chips: SUGGEST[s.key] || [],
    });
  });
  if (worst) {
    bnItems.unshift({
      dot: worst.status.dot,
      title: worst.name + " is the stage to fix first",
      body: "This stage's own conversion is the furthest below its benchmark — fixing it unlocks the most downstream volume.",
      chips: SUGGEST[worst.key] || [],
    });
  }
  if (wins.length > 0) {
    bnItems.push({
      dot: "#8CA30B",
      title: "What's working",
      body: "Above target: " + wins.map((s) => s.name.toLowerCase()).join(" and ") + ".",
      chips: [],
    });
  }

  // Drawer segment with client-side filter/sort.
  const dSt = drawer ? comp.find((s) => s.key === drawer) : null;
  let drawerUsers = [];
  if (dSt) {
    let list = drawerData;
    const ql = q.trim().toLowerCase();
    if (ql) list = list.filter((u) => (u.name || "").toLowerCase().includes(ql) || (u.email || "").toLowerCase().includes(ql));
    if (fPlan !== "All plans") list = list.filter((u) => u.plan === fPlan.toLowerCase());
    const val = (u) =>
      sortBy === "lastActive"
        ? -(daysAgo(u.last_active_at) ?? 99999)
        : sortBy === "signup"
        ? -(daysAgo(u.signup_at) ?? 99999)
        : sortBy === "walks"
        ? u.walks
        : u.streak;
    drawerUsers = [...list].sort((a, b) => (sortDir === "desc" ? val(b) - val(a) : val(a) - val(b)));
  }

  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const funnelSpan = comp.length > 0 ? comp[0].name + " → " + comp[comp.length - 1].name.toLowerCase() : "";

  return (
    <div style={{ minHeight: "100%", background: "#B9CC0310", fontFamily: "'ClashGrotesk', sans-serif", color: "#000" }}>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 24px 48px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 300 }}>Funnel Analytics</div>
            <div style={{ fontSize: 13.5, color: "#6B7051", marginTop: 2 }}>
              {funnelSpan ? funnelSpan + " · " : ""}data through {today}
            </div>
          </div>
          <div style={{ display: "flex", background: "#fff", border: "1px solid #E4E8CE", borderRadius: 10, padding: 3, gap: 2 }}>
            {[["all", "All time"], ["90d", "Last 90 days"], ["30d", "Last 30 days"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setRange(key)}
                style={{
                  border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, padding: "6px 14px", borderRadius: 8,
                  background: range === key ? "#B9CC03" : "transparent",
                  color: range === key ? "#2E3308" : "#6B7051",
                  fontWeight: range === key ? 600 : 400,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {hiddenStages.length > 0 && (
          <div style={{ background: "#F6F8E8", border: "1px solid #E4E8CE", borderRadius: 12, padding: "10px 14px", fontSize: 13, color: "#55660A" }}>
            Not tracked yet: <b>{hiddenStages.map((s) => s.name).join(", ")}</b> — these stages will appear automatically once install and trial tracking exist in the backend.
          </div>
        )}

        {error && (
          <div style={{ background: "#FBEAEA", border: "1px solid #F2CFCF", borderRadius: 12, padding: "12px 14px", fontSize: 13.5, color: "#8E2C2C" }}>
            Couldn't load funnel data: {error}
          </div>
        )}

        {loading && !counts && (
          <div style={{ padding: 60, textAlign: "center", color: "#8A9070", fontSize: 14 }}>Loading funnel data…</div>
        )}

        {counts && comp.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20, opacity: loading ? 0.55 : 1, transition: "opacity 0.15s" }}>

            {/* KPI cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
              {kpis.map((k) => (
                <div key={k.label} style={{ ...card, borderRadius: 14, padding: "16px 18px" }}>
                  <div style={{ fontSize: 13, color: "#6B7051", fontWeight: 500 }}>{k.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6 }}>{k.value}</div>
                  <div style={{ fontSize: 12.5, color: "#8A9070", marginTop: 3 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Funnel + Bottleneck summary */}
            <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 14, alignItems: "start" }}>
              <div style={card}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 17, fontWeight: 600 }}>Funnel · expected vs actual</div>
                  <div style={{ fontSize: 12, color: "#8A9070" }}>Dashed outline = target · click a stage for users</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", marginTop: 14 }}>
                  {comp.map((s, i) => {
                    const prev = i > 0 ? comp[i - 1] : null;
                    const stepActual = prev && prev.actual > 0 ? (s.actual / prev.actual) * 100 : 0;
                    const stepTarget = prev
                      ? benchMode === "step" ? activeBench[s.key] : prev.actual > 0 ? (s.expected / prev.actual) * 100 : 0
                      : null;
                    const convColor = prev ? (s.ratio < 0.75 ? "#C0392B" : s.ratio < 0.95 ? "#B45309" : "#6B7051") : "#6B7051";
                    const colorIdx = Math.round((i / Math.max(comp.length - 1, 1)) * (FUNNEL_COLORS.length - 1));
                    const textColor = colorIdx >= 4 ? "#fff" : "#2E3308";
                    return (
                      <div key={s.key}>
                        {prev && (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, height: 22, fontSize: 12, color: convColor, fontWeight: 500 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="4" x2="12" y2="20" /><polyline points="6 14 12 20 18 14" />
                            </svg>
                            <span>{pct1(stepActual) + " convert · target " + pct1(stepTarget)}</span>
                          </div>
                        )}
                        <div
                          onClick={() => openDrawer(s.key)}
                          style={{ position: "relative", height: 46, cursor: "pointer", borderRadius: 10 }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFBEF")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <div style={{ position: "absolute", top: 5, bottom: 5, left: "50%", transform: "translateX(-50%)", width: wOf(s.expected), border: "1.5px dashed #C4CB9A", borderRadius: 9 }} />
                          <div style={{ position: "absolute", top: 5, bottom: 5, left: "50%", transform: "translateX(-50%)", width: wOf(s.actual), background: FUNNEL_COLORS[colorIdx], borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, overflow: "visible", whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: textColor }}>{s.name}</span>
                            <span style={{ fontSize: 13, fontWeight: 400, color: textColor }}>{fmt(s.actual)}</span>
                          </div>
                          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.status.dot }} />
                            <span style={{ fontSize: 12, color: "#6B7051", width: 44, textAlign: "right" }}>{s.isBase ? "100%" : pct1(s.actualPct)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ ...card, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 17, fontWeight: 600 }}>Bottleneck summary</div>
                <div style={{ background: worst ? "#FBEAEA" : "#EEF4CD", border: worst ? "1px solid #F2CFCF" : "1px solid #DDE2C4", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: worst ? "#D64545" : "#8CA30B", flexShrink: 0 }} />
                    <span style={{ fontSize: 14.5, fontWeight: 600, color: worst ? "#8E2C2C" : "#55660A" }}>{bnHeadline}</span>
                  </div>
                  <div style={{ fontSize: 13, color: worst ? "#7A4A4A" : "#5F6547", marginTop: 5, lineHeight: 1.45 }}>{bnHeadlineBody}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {bnItems.map((b) => (
                    <div key={b.title} style={{ borderTop: "1px solid #F0F2E2", paddingTop: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: b.dot, flexShrink: 0 }} />
                        <span style={{ fontSize: 13.5, fontWeight: 600 }}>{b.title}</span>
                      </div>
                      <div style={{ fontSize: 13, color: "#5F6547", marginTop: 4, lineHeight: 1.45 }}>{b.body}</div>
                      {b.chips.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, alignItems: "center" }}>
                          <span style={{ fontSize: 11.5, color: "#8A9070", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.4 }}>Investigate</span>
                          {b.chips.map((c) => (
                            <span key={c} style={{ fontSize: 12, background: "#F6F8E8", border: "1px solid #E4E8CE", color: "#55660A", borderRadius: 999, padding: "3px 10px" }}>{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Detail table */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 17, fontWeight: 600 }}>Stage detail</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", gap: 2 }}>
                    {[["step", "% of previous step"], ["overall", "% of " + (comp[0]?.name.toLowerCase() || "baseline")]].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setBenchMode(key)}
                        style={{
                          border: benchMode === key ? "1px solid #DDE2C4" : "1px solid transparent",
                          cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "5px 10px", borderRadius: 8,
                          background: benchMode === key ? "#F6F8E8" : "transparent",
                          color: benchMode === key ? "#55660A" : "#8A9070",
                          fontWeight: benchMode === key ? 600 : 400,
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: "#8A9070" }}>Expected % is editable — benchmarks save automatically</div>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 1030 }}>
                  <div style={{ display: "grid", gridTemplateColumns: TABLE_GRID, padding: "8px 12px", fontSize: 12, color: "#6B7051", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #EDEFDD" }}>
                    <div>Funnel stage</div>
                    <div style={{ textAlign: "right" }}>Expected %</div>
                    <div style={{ textAlign: "right" }}>Actual %</div>
                    <div style={{ textAlign: "right" }}>Expected users</div>
                    <div style={{ textAlign: "right" }}>Actual users</div>
                    <div style={{ textAlign: "right" }}>Gap</div>
                    <div style={{ textAlign: "center" }}>Status</div>
                  </div>
                  {comp.map((s) => {
                    const gapStr = !s.hasTarget
                      ? "—"
                      : (s.gap >= 0 ? "+" + fmt(s.gap) : "−" + fmt(-s.gap)) +
                        " (" + (s.gap >= 0 ? "+" : "−") + Math.abs(Math.round((s.ratio - 1) * 100)) + "%)";
                    const gapColor = !s.hasTarget ? "#8A9070" : s.gap >= 0 ? "#55660A" : s.ratio < 0.75 ? "#C0392B" : "#B45309";
                    return (
                      <div
                        key={s.key}
                        onClick={() => openDrawer(s.key)}
                        style={{ display: "grid", gridTemplateColumns: TABLE_GRID, padding: "10px 12px", alignItems: "center", borderBottom: "1px solid #F4F6E6", cursor: "pointer", borderRadius: 8 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFBEF")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div>
                          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{s.name}</div>
                          <div style={{ fontSize: 12, color: "#8A9070", marginTop: 1 }}>{s.desc}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {s.isBase ? (
                            <>
                              <input
                                type="number"
                                value={baseTarget ?? ""}
                                placeholder={fmt(s.actual)}
                                onChange={(e) => setBaseTarget(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                min="0" step="100"
                                style={{ width: 90, padding: "5px 6px", border: "1px solid #DDE2C4", borderRadius: 8, fontFamily: "inherit", fontSize: 13.5, textAlign: "right", background: "#FDFEF6", color: "#000" }}
                              />
                              <div style={{ fontSize: 10.5, color: "#A9AF8E", marginTop: 2 }}>expected count</div>
                            </>
                          ) : (
                            <>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                                <input
                                  type="number"
                                  value={activeBench[s.key]}
                                  onChange={(e) => setBenchValue(s.key, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  min="0" max="100" step="0.5"
                                  style={{ width: 60, padding: "5px 6px", border: "1px solid #DDE2C4", borderRadius: 8, fontFamily: "inherit", fontSize: 13.5, textAlign: "right", background: "#FDFEF6", color: "#000" }}
                                />
                                <span style={{ fontSize: 13, color: "#6B7051" }}>%</span>
                              </span>
                              <div style={{ fontSize: 10.5, color: "#A9AF8E", marginTop: 2 }}>{s.baseNote}</div>
                            </>
                          )}
                        </div>
                        <div style={{ textAlign: "right", fontSize: 14 }}>{s.isBase ? "—" : pct1(s.actualPct)}</div>
                        <div style={{ textAlign: "right", fontSize: 14, color: "#6B7051" }}>{s.hasTarget ? fmt(s.expected) : "—"}</div>
                        <div style={{ textAlign: "right", fontSize: 14.5, fontWeight: 600 }}>{fmt(s.actual)}</div>
                        <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600, color: gapColor }}>{gapStr}</div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: s.status.bg, color: s.status.fg, borderRadius: 999, padding: "4px 11px", fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap" }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.status.dot }} />
                            {s.status.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stage drawer */}
      {dSt && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1300 }}>
          <div onClick={() => setDrawer(null)} style={{ position: "absolute", inset: 0, background: "rgba(30,32,18,0.32)", animation: "twFadeIn 0.18s ease" }} />
          <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 900, maxWidth: "94vw", background: "#fff", boxShadow: "-12px 0 40px rgba(0,0,0,0.16)", animation: "twDrawerIn 0.22s ease", display: "flex", flexDirection: "column" }}>

            <div style={{ padding: "20px 26px 16px 26px", borderBottom: "1px solid #EDEFDD", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: "#8A9070", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Funnel stage</div>
                <div style={{ fontSize: 23, fontWeight: 600, marginTop: 2 }}>{dSt.name}</div>
                <div style={{ fontSize: 13, color: "#6B7051", marginTop: 2 }}>{dSt.desc}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, color: "#8A9070" }}>Actual</div>
                  <div style={{ fontSize: 19, fontWeight: 600 }}>{fmt(dSt.actual)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, color: "#8A9070" }}>Expected</div>
                  <div style={{ fontSize: 19, fontWeight: 600, color: "#6B7051" }}>{dSt.hasTarget ? fmt(dSt.expected) : "—"}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, color: "#8A9070" }}>Gap</div>
                  <div style={{ fontSize: 19, fontWeight: 600, color: dSt.gap < 0 ? "#C0392B" : "#55660A" }}>
                    {dSt.hasTarget ? (dSt.gap >= 0 ? "+" : "−") + fmt(Math.abs(dSt.gap)) : "—"}
                  </div>
                </div>
                <button
                  onClick={() => setDrawer(null)}
                  style={{ border: "none", background: "#F4F6E6", borderRadius: 10, width: 36, height: 36, cursor: "pointer", fontSize: 16, color: "#55660A", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#E8ECCB")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#F4F6E6")}
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={{ padding: "14px 26px", borderBottom: "1px solid #EDEFDD", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Search name or email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ flex: 1, minWidth: 180, padding: "8px 12px", border: "1px solid #DDE2C4", borderRadius: 10, fontFamily: "inherit", fontSize: 13.5, background: "#FDFEF6", color: "#000" }}
              />
              <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} style={selectStyle}>
                <option>All plans</option><option>Premium</option><option>Free</option>
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
                  <option value="lastActive">Sort: Last active</option>
                  <option value="signup">Sort: Signup date</option>
                  <option value="walks">Sort: Walks</option>
                  <option value="streak">Sort: Streak</option>
                </select>
                <button
                  onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  title="Toggle direction"
                  style={{ border: "1px solid #DDE2C4", background: "#fff", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: 13, color: "#55660A" }}
                >
                  {sortDir === "desc" ? "↓" : "↑"}
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", padding: "0 26px 18px 26px" }}>
              <div style={{ minWidth: 700 }}>
                <div style={{ display: "grid", gridTemplateColumns: USER_GRID, columnGap: 10, padding: "12px 8px 8px 8px", fontSize: 11, color: "#6B7051", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #EDEFDD", position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
                  <div>User</div>
                  <div>Signup</div>
                  <div>Last active</div>
                  <div style={{ textAlign: "right" }}>Walks</div>
                  <div style={{ textAlign: "right" }}>Streak</div>
                  <div style={{ textAlign: "right" }}>Dogs</div>
                  <div>Plan</div>
                </div>
                {drawerLoading && (
                  <div style={{ padding: 40, textAlign: "center", color: "#8A9070", fontSize: 14 }}>Loading users…</div>
                )}
                {!drawerLoading && drawerUsers.map((u) => {
                  const la = daysAgo(u.last_active_at);
                  return (
                    <div key={u.id} style={{ display: "grid", gridTemplateColumns: USER_GRID, columnGap: 10, padding: "9px 8px", alignItems: "center", borderBottom: "1px solid #F4F6E6", fontSize: 12.5 }}>
                      <div style={{ overflow: "hidden" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                        <div style={{ color: "#8A9070", fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                      </div>
                      <div style={{ color: "#5F6547" }}>{shortDate(u.signup_at)}</div>
                      <div style={{ color: la == null ? "#A9AF8E" : la <= 7 ? "#55660A" : la <= 30 ? "#5F6547" : "#C0392B" }}>
                        {la == null ? "never" : la === 0 ? "today" : la === 1 ? "yesterday" : la + "d ago"}
                      </div>
                      <div style={{ textAlign: "right", fontWeight: 600 }}>{u.walks}</div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}>
                          {u.streak > 0 && <img src={FireIcon} alt="" style={{ width: 13, height: 13 }} />}
                          <span style={{ fontWeight: 600 }}>{u.streak}</span>
                        </span>
                      </div>
                      <div style={{ textAlign: "right" }}>{u.dogs}</div>
                      <div>
                        <span style={{
                          display: "inline-block", borderRadius: 999, padding: "2px 9px", fontSize: 11.5, fontWeight: 600,
                          background: u.plan === "premium" ? "#B9CC03" : "#F2F3EA",
                          color: u.plan === "premium" ? "#2E3308" : "#6B7051",
                        }}>
                          {u.plan === "premium" ? "Premium" : "Free"}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {!drawerLoading && drawerUsers.length === 0 && (
                  <div style={{ padding: 40, textAlign: "center", color: "#8A9070", fontSize: 14 }}>No users match these filters.</div>
                )}
                {!drawerLoading && drawerUsers.length > 0 && (
                  <div style={{ padding: "12px 8px 0 8px", fontSize: 12, color: "#A9AF8E" }}>
                    {drawerData.length >= 200
                      ? `Showing the 200 most recently active of ${fmt(dSt.actual)} users.`
                      : `${drawerUsers.length} of ${fmt(dSt.actual)} users shown.`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes twDrawerIn { from { transform: translateX(48px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes twFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default FunnelAnalytics;
